import { assign, createActor, fromPromise, type StateValue, type ActorRefFrom } from 'xstate';
import { orderMachine, OrderMachineContext } from '../machines/orderMachine';
import { Order, OrderSchema, SupervisorEvent } from '../types/supervisor';
import { store } from '@/app/store';
import { addMessage, sendMessage } from '@/features/chat/chatSlice';
import { getAiProposedUpdate } from '@/services/api';
import { setSnapshot } from '@/features/supervisor/supervisorSlice';
import type { SupervisorSnapshotPayload } from '@/features/supervisor/supervisorSlice';
import {
  selectHasMeaningfulItems,
  selectSelectedItemIds,
  setItemSelection,
  setOrderState,
  setShippingAddress,
  fetchShippingQuotes,
  createOrderForCheckout,
  syncOrderForCheckout,
  selectShippingQuoteStatus,
  hasPersistedOrderId,
} from '@/features/order/orderSlice';
import i18n from '@/i18n';
import { isOrderFlowItemStatus } from '@/types/orderStatus';
import {
  fetchPendingOrderItems,
  fetchDefaultShippingAddress,
  type PendingOrderItem,
  type PendingOrderSnapshot,
} from '@/services/orderService';
import {
  CONTINUE_WITH_EXISTING_LABEL,
  PENDING_IMAGES_MESSAGE,
  UPLOAD_ADDITIONAL_LABEL,
} from '@/constants/orderWorkflow';
import { logging } from '@/services/logging';
import { buildCanonicalOrder } from '@/features/order/utils/canonicalOrder';
import { toast } from '@/hooks/use-toast';

type OrderApplyPatchEvent = Extract<SupervisorEvent, { type: 'ORDER.APPLY_PATCH' }>;
type UploadSummaryEvent = Extract<SupervisorEvent, { type: 'UI.ANNOUNCE_UPLOAD_SUMMARY' }>;
type ShippingAddress = NonNullable<Order['shippingAddress']>;

const isOrderedItemStatus = (value: unknown): boolean => isOrderFlowItemStatus(value);

const isOrderApplyPatchEvent = (event: unknown): event is OrderApplyPatchEvent => {
  return (
    !!event &&
    typeof event === 'object' &&
    'type' in event &&
    (event as { type?: unknown }).type === 'ORDER.APPLY_PATCH'
  );
};

const normalizePendingItems = (items: ReadonlyArray<PendingOrderItem>): Order['items'] => {
  return items.map((item) => {
    return {
      id: item.id,
      ...(typeof item.uploadedFileId === 'string' && item.uploadedFileId.length > 0
        ? { uploadedFileId: item.uploadedFileId }
        : {}),
      quantity: typeof item.quantity === 'number' && Number.isFinite(item.quantity)
        ? Math.min(99, Math.max(1, Math.round(item.quantity)))
        : 1,
      paperTypeId: item.paperTypeId ?? undefined,
      printerId: item.printerId ?? undefined,
      widthIn: item.widthIn ?? undefined,
      heightIn: item.heightIn ?? undefined,
      marginIn: item.marginIn ?? undefined,
      galleryWrapIn: item.galleryWrapIn ?? undefined,
      galleryWrapMirrorEdges: item.galleryWrapMirrorEdges ?? undefined,
      colorMode: item.colorMode ?? undefined,
      status: item.status ?? 'NEW',
      orderId: item.orderId ?? null,
      originalWidthPx: item.originalWidthPx ?? undefined,
      originalHeightPx: item.originalHeightPx ?? undefined,
      originalWidthIn: item.originalWidthIn ?? undefined,
      originalHeightIn: item.originalHeightIn ?? undefined,
      dpiX: item.dpiX ?? undefined,
      dpiY: item.dpiY ?? undefined,
      printQualityWarning: item.printQualityWarning ?? undefined,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      price: item.priceUsd != null
        ? {
            value: item.priceUsd,
            currency: 'USD',
          }
        : undefined,
      pricingStatus: item.priceUsd != null ? 'succeeded' : 'idle',
      pricingError: null,
    } satisfies Order['items'][number];
  });
};

const getSnapshotFromDoneEvent = (event: unknown): PendingOrderSnapshot | null => {
  if (!event || typeof event !== 'object') {
    return null;
  }

  const { output, data } = event as { output?: unknown; data?: unknown };

  logging.info('[supervisor] fetchPendingOrders done event', { event });

  if (output && typeof output === 'object') {
    return output as PendingOrderSnapshot;
  }

  if (data && typeof data === 'object') {
    return data as PendingOrderSnapshot;
  }

  return null;
};

const normalizePendingSnapshot = (
  snapshot: PendingOrderSnapshot | null,
): {
  readonly items: Order['items'];
  readonly metadata: Record<string, unknown>;
} => {
  if (!snapshot) {
    return { items: [], metadata: {} };
  }

  const { order } = snapshot;
  const items = normalizePendingItems(order.items ?? []);

  const metadata: Record<string, unknown> = {};

  if (order.id) {
    metadata.id = order.id;
  }
  if (order.status) {
    metadata.status = order.status;
  }
  if (order.packaging !== undefined) {
    metadata.packaging = order.packaging;
  }
  if (order.comment !== undefined) {
    metadata.comment = order.comment;
  }
  if (order.createdAt) {
    metadata.createdAt = order.createdAt;
  }
  if (order.updatedAt) {
    metadata.updatedAt = order.updatedAt;
  }
  if (order.paymentInformation !== undefined) {
    metadata.paymentInformation = order.paymentInformation;
  }

  if (order.shippingAddress !== undefined) {
    metadata.shippingAddress = order.shippingAddress ?? undefined;
  }
  if (order.fulfillmentMode !== undefined) {
    metadata.fulfillmentMode = order.fulfillmentMode;
  }

  return { items, metadata };
};

// ===========================================================================
// Action Implementations
// ===========================================================================

const REQUIRED_ADDRESS_FIELDS: ReadonlyArray<keyof ShippingAddress> = [
  'countryCode',
  'firstName',
  'lastName',
  'phone',
  'street1',
  'city',
  'region',
  'postalCode',
];

export const isShippingAddressComplete = (address?: Order['shippingAddress']): address is ShippingAddress => {
  if (!address) {
    return false;
  }

  return REQUIRED_ADDRESS_FIELDS.every((field) => {
    const value = address[field];
    return typeof value === 'string' && value.trim().length > 0;
  });
};

let defaultAddressFetchInFlight: Promise<void> | null = null;
let shippingQuoteFetchInFlight: Promise<void> | null = null;
let orderSyncInFlight: Promise<void> | null = null;

const actions = {
  applyPatchSafely: assign(({ context, event }) => {
    if (!isOrderApplyPatchEvent(event)) {
      return {};
    }

    const validationResult = OrderSchema.partial().safeParse(event.patch);
    if (!validationResult.success) {
      logging.error('Invalid patch received from AI', { error: validationResult.error });
      return {};
    }

    const patch = validationResult.data;

    const nextOrder: Order = {
      ...context.order,
      ...patch,
      items: Array.isArray(patch.items) ? patch.items : context.order.items,
    };

    return { order: nextOrder } satisfies Partial<OrderMachineContext>;
  }),

  persistLocalStorage: ({ context }) => {
    const mergedOrder = buildCanonicalOrder(store.getState().order, context.order as Order & Record<string, unknown>);
    store.dispatch(setOrderState(mergedOrder as any));
  },

  recomputeCompleteness: () => {
    // logging.info('Recomputing completeness for order', { order: context.order });
    // TODO: Implement the actual completeness logic based on the order state.
  },

  // --- UI-related Actions ---
  ensureUploadReady: () => logging.info('Ensuring Upload UI is ready...'),
  ensureSizeReady: ({ context }) => {
    const state = store.getState();
    const order = context.order;
    const items = Array.isArray(order?.items)
      ? order.items.filter((item): item is Order['items'][number] => Boolean(item && typeof item.id === 'string' && item.id.length > 0))
      : [];
    const selectedIds = selectSelectedItemIds(state);
    const activeSelections = Array.isArray(selectedIds) ? selectedIds.filter(Boolean) : [];

    if (items.length === 0) {
      store.dispatch(
        addMessage({
          author: 'ai',
          content: 'Please upload at least one image before setting its print size.',
        }),
      );
      const currentSnapshot = store.getState().supervisor.snapshot;
      store.dispatch(setSnapshot({
        value: context.activeStep,
        context: { ...context, activeStep: 'upload' },
        loginPromptSent: currentSnapshot?.loginPromptSent ?? false,
      }));
      store.dispatch(setOrderState({ ...order } as any));
      supervisorService.send({ type: 'UI.GOTO_STEP', step: 'upload' });
      return;
    }

    if (items.length === 1) {
      const identifier = items[0]?.id;
      if (identifier) {
        store.dispatch(setItemSelection({ itemId: identifier, selected: true }));
      }
      return;
    }

    if (activeSelections.length > 0) {
      const target = items.find(item => typeof item.id === 'string' && activeSelections.includes(item.id));
      if (target) {
        const identifier = target.id;
        store.dispatch(setItemSelection({ itemId: identifier, selected: true }));
        return;
      }
    }

    store.dispatch(
      addMessage({
        author: 'ai',
        content:
          "To adjust the print size, please select one of the uploaded images first. You can also open the image menu (three dots) and choose 'Set print size'.",
      }),
    );

    const hasMeaningfulItems = selectHasMeaningfulItems(state);
    const fallbackStep = hasMeaningfulItems ? 'uploadedImages' : 'upload';
    supervisorService.send({ type: 'UI.GOTO_STEP', step: fallbackStep });
  },
  ensurePrintersLoaded: () => logging.info('Ensuring Printers are loaded...'),
  ensurePapersLoaded: () => logging.info('Ensuring Papers are loaded...'),
  ensureAddressReady: ({ context }) => {
    const state = store.getState();
    const machineAddress = context.order?.shippingAddress as Order['shippingAddress'] | undefined;
    const reduxAddress = state.order.shippingAddress;
    const fulfillmentMode = context.order?.fulfillmentMode ?? state.order.fulfillmentMode;

    if (fulfillmentMode === 'PICKUP' || isShippingAddressComplete(machineAddress ?? reduxAddress)) {
      return;
    }

    if (defaultAddressFetchInFlight) {
      return;
    }

    defaultAddressFetchInFlight = (async () => {
      try {
        const fetched = await fetchDefaultShippingAddress();
        if (!fetched) {
          return;
        }

        const { id: _ignoredId, setAsDefault: setAsDefaultFlag, ...rest } = fetched;
        const normalized: ShippingAddress = {
          ...rest,
          ...(setAsDefaultFlag !== undefined ? { setAsDefault: setAsDefaultFlag } : {}),
        };

        const latestState = store.getState();
        if (isShippingAddressComplete(latestState.order.shippingAddress)) {
          return;
        }

        store.dispatch(setShippingAddress(normalized));
        supervisorService.send({ type: 'ORDER.APPLY_PATCH', patch: { shippingAddress: normalized } });
      } catch (error) {
        logging.error('[supervisor] Failed to fetch default shipping address', { error });
      } finally {
        defaultAddressFetchInFlight = null;
      }
    })();
  },
  ensurePaymentUi: ({ context }) => {
    if (context.activeStep !== 'payment') {
      return;
    }
    const state = store.getState();
    const reduxAddress = state.order.shippingAddress;
    const machineAddress = context.order?.shippingAddress as Order['shippingAddress'] | undefined;
    const fulfillmentMode = context.order?.fulfillmentMode ?? state.order.fulfillmentMode;

    const effectiveAddress = machineAddress ?? reduxAddress;
    logging.info('[supervisor] ensurePaymentUi invoked', {
      machineAddressPresent: Boolean(machineAddress),
      reduxAddressPresent: Boolean(reduxAddress),
      effectiveAddress,
    });

    if (fulfillmentMode !== 'PICKUP' && !isShippingAddressComplete(effectiveAddress)) {
      logging.warn('[supervisor] Payment step blocked due to incomplete address', {
        machineAddress,
        reduxAddress,
      });
      console.warn('[supervisor] Payment step blocked due to incomplete address', {
        machineAddress,
        reduxAddress,
      });
      store.dispatch(
        addMessage({
          author: 'ai',
          content:
            'Please provide a full shipping address before proceeding to payment. Once it is complete, we will automatically prepare your order.',
        }),
      );
      supervisorService.send({ type: 'UI.GOTO_STEP', step: 'address' });
      return;
    }

    if (orderSyncInFlight) {
      logging.info('[supervisor] Order sync already in progress, skipping duplicate ensurePaymentUi invocation');
      return;
    }

    const performOrderSync = async (): Promise<void> => {
      try {
        const currentState = store.getState();
        if (currentState.order.checkoutPersistenceInFlight) {
          logging.info('[supervisor] Checkout persistence already in flight, skipping duplicate ensurePaymentUi invocation');
          return;
        }
        const orderId = currentState.order.id;

        const orderedItems = currentState.order.items.filter((item) => isOrderedItemStatus(item?.status));
        if (orderedItems.length === 0) {
          logging.warn('[supervisor] Payment step blocked because no items are marked for order', {
            itemCount: currentState.order.items.length,
            itemStatuses: currentState.order.items.map((item) => ({
              id: item?.id,
              status: item?.status ?? null,
            })),
          });
          store.dispatch(
            addMessage({
              author: 'ai',
              content: 'Please add at least one configured image to your order before proceeding to payment.',
            }),
          );
          supervisorService.send({ type: 'UI.GOTO_STEP', step: 'review' });
          return;
        }

        if (hasPersistedOrderId(orderId)) {
          const action = await store.dispatch(syncOrderForCheckout());
          if (syncOrderForCheckout.fulfilled.match(action)) {
            logging.info('[supervisor] ensurePaymentUi syncOrderForCheckout succeeded', {
              orderId: action.payload.orderId,
              resultingOrderIdInStore: store.getState().order.id,
              itemCount: store.getState().order.items.length,
            });
          } else {
            logging.error('[supervisor] ensurePaymentUi syncOrderForCheckout failed', {
              error: action.payload ?? action.error,
              orderIdBeforeSync: orderId,
            });
            const message = typeof action.error?.message === 'string' && action.error.message.length > 0
              ? action.error.message
              : 'syncOrderForCheckout failed';
            throw new Error(message);
          }
        } else {
          const action = await store.dispatch(createOrderForCheckout());
          if (createOrderForCheckout.fulfilled.match(action)) {
            logging.info('[supervisor] ensurePaymentUi createOrderForCheckout succeeded', {
              createdOrderId: action.payload.orderId,
              resultingOrderIdInStore: store.getState().order.id,
              itemCount: store.getState().order.items.length,
            });
          } else {
            logging.error('[supervisor] ensurePaymentUi createOrderForCheckout failed', {
              error: action.payload ?? action.error,
              orderIdBeforeCreate: orderId ?? null,
            });
            const message = typeof action.error?.message === 'string' && action.error.message.length > 0
              ? action.error.message
              : 'createOrderForCheckout failed';
            throw new Error(message);
          }
        }

        const postSyncState = store.getState();

        if (postSyncState.order.fulfillmentMode === 'PICKUP') {
          return;
        }

        if (!isShippingAddressComplete(postSyncState.order.shippingAddress)) {
          logging.warn('[supervisor] Skipping shipping quote fetch due to incomplete address after sync');
          console.warn('[supervisor] Skipping shipping quote fetch due to incomplete address after sync');
          return;
        }

        const quoteStatus = selectShippingQuoteStatus(postSyncState);
        if (quoteStatus === 'loading' || shippingQuoteFetchInFlight) {
          logging.info('[supervisor] Shipping quote fetch already in progress, skipping duplicate dispatch');
          return;
        }

        const startShippingQuoteFetch = async (): Promise<void> => {
          try {
            await store.dispatch(fetchShippingQuotes()).unwrap();
          } catch (error) {
            logging.error('[supervisor] Failed to fetch Ukrposhta shipping quote after order sync', { error });
            console.error('[supervisor] Failed to fetch Ukrposhta shipping quote after order sync', { error });
          } finally {
            shippingQuoteFetchInFlight = null;
          }
        };

        shippingQuoteFetchInFlight = startShippingQuoteFetch()
          .catch((error) => {
            logging.error('[supervisor] Unexpected failure while fetching shipping quote', { error });
            console.error('[supervisor] Unexpected failure while fetching shipping quote', { error });
          })
          .finally(() => {
            shippingQuoteFetchInFlight = null;
          });
      } catch (error) {
        logging.error('[supervisor] ensurePaymentUi failed during order sync', { error });
        console.error('[supervisor] ensurePaymentUi failed during order sync', { error });
        store.dispatch(
          addMessage({
            author: 'ai',
            content: 'We could not sync your order before payment. Please review your items and try again.',
          }),
        );
        const currentStep = supervisorService.getSnapshot().context.activeStep;
        if (currentStep === 'payment') {
          supervisorService.send({ type: 'UI.GOTO_STEP', step: 'review' });
        }
      }
    };

    orderSyncInFlight = performOrderSync()
      .catch((error) => {
        logging.error('[supervisor] ensurePaymentUi encountered an unexpected error', { error });
        console.error('[supervisor] ensurePaymentUi encountered an unexpected error', { error });
      })
      .finally(() => {
        orderSyncInFlight = null;
      });
  },
  // ensureSummary: () => logging.info('Ensuring Summary is ready...'),
  logError: ({ event }) => {
    if (typeof event?.type === 'string' && event.type.endsWith('.ERROR')) {
      const error = 'data' in event ? event.data : event;
      logging.error('Supervisor error', { error });
    }
  },

  applyPendingOrderResult: assign(({ context, event }) => {
    const snapshot = getSnapshotFromDoneEvent(event);
    const { items, metadata } = normalizePendingSnapshot(snapshot);
    logging.info('[supervisor] applyPendingOrderResult', {
      itemCount: items.length,
      metadata,
    });

    if (items.length === 0) {
      return {
        order: {
          ...context.order,
          ...metadata,
          items: [],
        },
        activeStep: 'upload',
      } satisfies Partial<OrderMachineContext>;
    }

    const nextOrder = {
      ...context.order,
      ...metadata,
      items,
    } as Order & Record<string, unknown>;

    return {
      order: nextOrder,
      activeStep: 'uploadedImages',
    } satisfies Partial<OrderMachineContext>;
  }),

  announcePendingItems: (_context, event) => {
    const snapshot = getSnapshotFromDoneEvent(event);
    const { items } = normalizePendingSnapshot(snapshot);
    if (items.length === 0) {
      return;
    }
    store.dispatch(
      addMessage({
        author: 'ai',
        content: PENDING_IMAGES_MESSAGE,
        suggestedActions: [CONTINUE_WITH_EXISTING_LABEL, UPLOAD_ADDITIONAL_LABEL],
      })
    );
  },
  notifyUploadSummary: ({ event }) => {
    if ((event as SupervisorEvent).type !== 'UI.ANNOUNCE_UPLOAD_SUMMARY') {
      return;
    }

    const { summaryText } = event as UploadSummaryEvent;

    const trimmedSummary = summaryText.trim();
    if (!trimmedSummary) {
      return;
    }

    void store.dispatch(sendMessage(trimmedSummary));
  },
  handleAiPrompt: ({ event }) => {
    if (event?.type !== 'UI.REQUEST_AI_PROMPT') {
      return;
    }

    const aiPrompt = event.aiPrompt?.trim();
    if (!aiPrompt) {
      logging.warn('[Supervisor] Received AI prompt request without prompt text');
      return;
    }

    toast({
      title: 'AI response incoming',
      description: 'Please check the chat. The AI will explain what happened and the next steps.',
    });

    const userLanguage = i18n.language;
    const localizedInstruction = `${aiPrompt} Reply in the user's language identified by the code "${userLanguage}".`;
    const fallbackMessage = event.fallbackMessage?.trim();

    void (async () => {
      try {
        const conversationHistory = [{ role: 'user' as const, content: localizedInstruction }];
        const proposed = await getAiProposedUpdate(
          conversationHistory,
          store.getState().order as unknown as Record<string, unknown>,
          userLanguage,
          { source: 'interface' },
        );

        const utterance = proposed.utterance?.trim();
        const suggestedActions = event.suggestedActions ?? proposed.suggestedActions;

        if (utterance) {
          store.dispatch(
            addMessage({
              author: 'ai',
              content: utterance,
              ...(suggestedActions ? { suggestedActions } : {}),
            }),
          );
          return;
        }
      } catch (error) {
        logging.error('[Supervisor] Failed to fetch AI prompt response', { error });
      }

      if (fallbackMessage) {
        store.dispatch(
          addMessage({
            author: 'ai',
            content: fallbackMessage,
            ...(event.suggestedActions ? { suggestedActions: event.suggestedActions } : {}),
          }),
        );
      }
    })();
  },
} satisfies Parameters<typeof orderMachine.provide>[0]['actions'];

// ===========================================================================
// Supervisor Service
// ===========================================================================

export const supervisorMachine = orderMachine.provide({
  actions,
  actors: {
    fetchPendingOrders: fromPromise(async () => {
      const snapshot = await fetchPendingOrderItems();
      const normalized = normalizePendingSnapshot(snapshot);

      logging.info('[supervisor] fetched pending items', {
        pendingCount: snapshot.order.items.length,
        normalizedCount: normalized.items.length,
        orderId: snapshot.order.id,
        orderStatus: snapshot.order.status,
      });

      return snapshot;
    }),
  },
});

type SupervisorActor = ReturnType<typeof createActor>;

interface SupervisorRuntime {
  readonly service: SupervisorActor;
  loginAiPromptSent: boolean;
  subscription?: { unsubscribe: () => void };
  initialDispatchDone?: boolean;
}

declare global {
  interface Window {
    __supervisorRuntime?: SupervisorRuntime;
  }
}

const readPersistedSupervisorSnapshot = (): SupervisorSnapshotPayload | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const persistedRoot = window.localStorage.getItem('persist:root');
    if (!persistedRoot) {
      return null;
    }

    const parsedRoot = JSON.parse(persistedRoot) as Record<string, string>;
    const serializedSupervisor = parsedRoot.supervisor;
    if (!serializedSupervisor) {
      return null;
    }

    const parsedSupervisor = JSON.parse(serializedSupervisor) as { snapshot: SupervisorSnapshotPayload | null };
    return parsedSupervisor.snapshot ?? null;
  } catch (error) {
    logging.warn('Failed to read persisted supervisor snapshot. Falling back to defaults.', { error });
    return null;
  }
};

const mergeContextWithDefaults = (incoming: OrderMachineContext): OrderMachineContext => {
  const defaults = (orderMachine.config.context ?? {}) as OrderMachineContext;
  const mergedOrder = buildCanonicalOrder(store.getState().order, incoming.order as Order & Record<string, unknown>);

  return {
    order: {
      ...(defaults.order ?? { items: [] }),
      ...mergedOrder,
      items: Array.isArray(mergedOrder?.items) ? mergedOrder.items : defaults.order?.items ?? [],
    },
    activeStep: incoming.activeStep ?? defaults.activeStep ?? 'upload',
    completeness: {
      ...(defaults.completeness ?? {}),
      ...(incoming.completeness ?? {}),
    },
  } satisfies OrderMachineContext;
};

const buildSupervisorActorOptions = (
  snapshotPayload: SupervisorSnapshotPayload | null | undefined,
): Parameters<typeof createActor>[1] | undefined => {
  if (!snapshotPayload) {
    return undefined;
  }

  try {
    const mergedContext = mergeContextWithDefaults(snapshotPayload.context);
    return {
      snapshot: supervisorMachine.resolveState({
        value: snapshotPayload.value,
        context: mergedContext,
      }),
    };
  } catch (error) {
    logging.warn('Failed to restore supervisor machine snapshot. Starting from initial state.', { error });
    return undefined;
  }
};

const persistedSnapshot = readPersistedSupervisorSnapshot();

let runtime: SupervisorRuntime;

const createSupervisorRuntime = (
  snapshotPayload: SupervisorSnapshotPayload | null | undefined,
): SupervisorRuntime => {
  const supervisorActorOptions = buildSupervisorActorOptions(snapshotPayload);
  const service = supervisorActorOptions
    ? createActor(supervisorMachine, supervisorActorOptions)
    : createActor(supervisorMachine);

  service.start();

  return {
    service,
    loginAiPromptSent: snapshotPayload?.loginPromptSent ?? false,
    initialDispatchDone: false,
  };
};

const syncRuntimeToWindow = (nextRuntime: SupervisorRuntime): void => {
  if (typeof window !== 'undefined') {
    window.__supervisorRuntime = nextRuntime;
  }
};

const obtainRuntime = (): SupervisorRuntime => {
  if (typeof window !== 'undefined' && window.__supervisorRuntime) {
    const existing = window.__supervisorRuntime;
    const snapshot = existing.service.getSnapshot();
    if (snapshot.status !== 'active') {
      existing.service.start();
      existing.initialDispatchDone = false;
    }
    return existing;
  }

  const created = createSupervisorRuntime(persistedSnapshot);
  syncRuntimeToWindow(created);
  return created;
};

runtime = obtainRuntime();

export type SupervisorActorRef = ActorRefFrom<typeof supervisorMachine>;

const supervisorActor = runtime.service as SupervisorActorRef;

export const supervisorService: SupervisorActorRef = new Proxy(supervisorActor, {
  get(target, prop, receiver) {
    if (prop === 'send' || prop === 'getSnapshot' || prop === 'subscribe') {
      const ensure = obtainRuntime().service;
      const value = Reflect.get(ensure, prop, receiver);
      if (prop === 'send' && typeof value === 'function') {
        const send = value as (event: SupervisorEvent) => unknown;
        return (event: SupervisorEvent) => send.call(ensure, event);
      }
      return typeof value === 'function' ? value.bind(ensure) : value;
    }
    return Reflect.get(target, prop, receiver);
  },
}) as SupervisorActorRef;

// Persist login prompt flag alongside machine state
let loginAiPromptSent = runtime.loginAiPromptSent;

const dispatchSnapshot = (value: StateValue, context: OrderMachineContext) => {
  store.dispatch(
    setSnapshot({
      value,
      context,
      loginPromptSent: loginAiPromptSent,
    })
  );
  try {
    localStorage.removeItem('in-progress-order');
  } catch (error) {
    logging.warn('Failed to remove legacy in-progress-order cache', { error });
  }
};

const deriveStateName = (value: any): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const [top, sub] = Object.entries(value)[0] as [string, any];
    if (typeof sub === 'string') return `${top}.${sub}`;
    return top;
  }
  return 'screen.upload';
};

const ensureRuntimeSubscription = (targetRuntime: SupervisorRuntime): void => {
  if (targetRuntime.subscription) {
    return;
  }

  targetRuntime.subscription = targetRuntime.service.subscribe((state) => {
    const derived = deriveStateName(state.value as any);
    const inLogin = derived === 'login' || derived === 'screen.login';

    // Fire a single AI nudge when we enter login state
    if (inLogin && !loginAiPromptSent) {
      loginAiPromptSent = true;
      targetRuntime.loginAiPromptSent = true;
      // Ask AI to present a friendly login/registration prompt in the user's language
      const userLanguage = i18n.language;
      const instruction =
        `The user now sees the login/registration form. Please politely ask them to log in or register to continue with creating the print order. Reply in the user's language identified by the code "${userLanguage}".`;
      // Call AI directly to generate a context-aware prompt and post it as an AI message
      void (async () => {
        try {
          const conversationHistory = [
            { role: 'user' as const, content: instruction },
          ];
          const proposed = await getAiProposedUpdate(
            conversationHistory,
            state.context.order as Record<string, unknown>,
            i18n.language,
            { source: 'interface' },
          );

          store.dispatch(
            addMessage({
              author: 'ai',
              content: proposed.utterance,
              suggestedActions: proposed.suggestedActions,
            })
          );
        } catch (e) {
          logging.error('Failed to fetch AI login prompt', { error: e });
        }
      })();
    }

    // Reset the flag when we leave login
    if (!inLogin && loginAiPromptSent) {
      loginAiPromptSent = false;
      targetRuntime.loginAiPromptSent = false;
    }

    dispatchSnapshot(state.value, state.context);
  });
};

const ensureRuntimeInitialized = (targetRuntime: SupervisorRuntime): void => {
  loginAiPromptSent = targetRuntime.loginAiPromptSent;
  syncRuntimeToWindow(targetRuntime);
  ensureRuntimeSubscription(targetRuntime);

  if (!targetRuntime.initialDispatchDone) {
    const initialSnapshot = targetRuntime.service.getSnapshot();
    dispatchSnapshot(initialSnapshot.value, initialSnapshot.context);
    targetRuntime.initialDispatchDone = true;
  }
};

ensureRuntimeInitialized(runtime);

export const getCurrentSupervisorSnapshotPayload = (): SupervisorSnapshotPayload => {
  const snapshot = obtainRuntime().service.getSnapshot();
  return {
    value: snapshot.value,
    context: snapshot.context,
    loginPromptSent: loginAiPromptSent,
  };
};

export const createInitialSupervisorSnapshotPayload = (): SupervisorSnapshotPayload => {
  const actor = createActor(supervisorMachine);
  const snapshot = actor.getSnapshot();

  return {
    value: snapshot.value,
    context: snapshot.context,
    loginPromptSent: false,
  };
};

export const rehydrateSupervisorSnapshot = (
  snapshotPayload: SupervisorSnapshotPayload | null,
): void => {
  const existingRuntime = typeof window !== 'undefined' ? window.__supervisorRuntime : runtime;

  if (existingRuntime?.subscription) {
    existingRuntime.subscription.unsubscribe();
    existingRuntime.subscription = undefined;
  }

  if (existingRuntime?.service) {
    existingRuntime.service.stop();
  }

  runtime = createSupervisorRuntime(snapshotPayload);
  ensureRuntimeInitialized(runtime);
};

let previousIsAuthenticated = store.getState().auth.isAuthenticated;

store.subscribe(() => {
  const state = store.getState();
  const { isAuthenticated } = state.auth;

  if (!previousIsAuthenticated && isAuthenticated) {
    supervisorService.send({ type: 'AUTH.LOGGED_IN' });
  } else if (previousIsAuthenticated && !isAuthenticated) {
    supervisorService.send({ type: 'AUTH.LOGGED_OUT' });
  }

  previousIsAuthenticated = isAuthenticated;
});
